import { toDisplayString as _toDisplayString, vShow as _vShow, createElementVNode as _createElementVNode, withDirectives as _withDirectives, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock, Fragment as _Fragment } from "vue";
const _hoisted_1 = {
  class: "remove-all-input-styling"
};
const _hoisted_2 = {
  key: 0,
  class: "flex flex-row items-start gap-3"
};
const _hoisted_3 = {
  class: "flex-1 min-w-0"
};
const _hoisted_4 = {
  class: "w-full pt-2"
};
import { ref, reactive, onMounted, watch } from 'vue';
import LlDatePicker from '@/components/LlDatePicker.vue';
import LlSelect from '@/components/LlSelect.vue';
import { getDatesInRange } from '@/services/utils.js';
export default {
  __name: 'DateRangeSelect',
  props: {
    rangeHideTitle: {
      type: Boolean,
      required: false
    },
    defaultRange: {
      type: String,
      default: 'Time Period',
      required: false
    },
    rangeOnlyMonthly: {
      type: Boolean,
      default: false
    },
    initialRange: {
      type: String,
      default: 'allTime',
      required: false
    },
    layout: {
      type: String,
      default: 'vertical',
      validator: value => ['horizontal', 'vertical'].includes(value)
    }
  },
  emits: ['dateRangeFilter', 'customPeriodToggle'],
  setup(__props, {
    emit: __emit
  }) {
    const props = __props;
    const emit = __emit;

    // Reactive state
    const period = ref(false); // whether we're in “custom” mode
    const selectedId = ref('allTime'); // which preset is selected
    const customDates = ref([]); // array from <ll-date-picker> in custom mode
    const form = reactive({
      range: {
        startDate: null,
        endDate: null
      }
    });
    const periods = ref([]);

    // Helper to convert a month index to a string
    function monthString(month) {
      return ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'][month];
    }

    // onMounted => build your preset ranges
    onMounted(() => {
      const today = new Date();
      const endOfPreviousMonth = new Date(today.getFullYear(), today.getMonth(), 0);
      const previousMonthString = `${monthString(endOfPreviousMonth.getMonth())} ${endOfPreviousMonth.getFullYear()}`;
      const startOfThisWeek = new Date(today.getFullYear(), today.getMonth(), today.getDate() - today.getDay());
      const endOfThisWeek = new Date(today.getFullYear(), today.getMonth(), today.getDate() - today.getDay() + 7);
      const startOfLastWeek = new Date(today.getFullYear(), today.getMonth(), today.getDate() - today.getDay() - 7);
      periods.value = [{
        label: 'All Time',
        value: 'allTime',
        range: {
          startDate: null,
          endDate: null
        }
      }, {
        label: 'Up to Today',
        value: 'upToToday',
        range: {
          startDate: null,
          endDate: new Date(today.getFullYear(), today.getMonth(), today.getDate())
        }
      }, {
        label: 'This Year',
        value: 'thisYear',
        range: {
          startDate: new Date(today.getFullYear(), 0, 1),
          endDate: new Date(today.getFullYear(), 11, 31)
        }
      }, {
        label: 'Last Year',
        value: 'lastYear',
        range: {
          startDate: new Date(today.getFullYear() - 1, 0, 1),
          endDate: new Date(today.getFullYear() - 1, 11, 31)
        }
      }, {
        label: previousMonthString + ' and earlier',
        value: 'previousMonth',
        range: {
          startDate: null,
          endDate: endOfPreviousMonth
        }
      }, {
        label: previousMonthString,
        // e.g. "February 2025"
        value: 'lastMonth',
        range: {
          startDate: new Date(endOfPreviousMonth.getFullYear(), endOfPreviousMonth.getMonth(), 1),
          endDate: endOfPreviousMonth
        }
      }, {
        label: 'This Month',
        value: 'thisMonth',
        range: {
          startDate: new Date(today.getFullYear(), today.getMonth(), 1),
          endDate: new Date(today.getFullYear(), today.getMonth() + 1, 0)
        }
      }];

      // Optionally add weeks if not monthly-only
      if (!props.rangeOnlyMonthly) {
        periods.value.push({
          label: 'This Week',
          value: 'thisWeek',
          range: {
            startDate: startOfThisWeek,
            endDate: endOfThisWeek
          }
        }, {
          label: 'Last Week',
          value: 'lastWeek',
          range: {
            startDate: startOfLastWeek,
            endDate: startOfThisWeek
          }
        }, {
          label: 'Today',
          value: 'today',
          range: {
            startDate: new Date(today.getFullYear(), today.getMonth(), today.getDate()),
            endDate: new Date(today.getFullYear(), today.getMonth(), today.getDate())
          }
        }, {
          label: 'Yesterday',
          value: 'yesterday',
          range: {
            startDate: new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1),
            endDate: new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1)
          }
        });
      }
      // Finally add the custom option
      periods.value.push({
        label: 'Custom Period',
        value: 'custom',
        custom: true
      });

      // Set initial selection based on prop
      selectedId.value = props.initialRange;
    });

    // 1) Watch selectedId => if “custom,” show date picker, otherwise set range
    watch(selectedId, newVal => {
      if (newVal === 'custom') {
        period.value = true; // Emit that custom period is now active
        emit('customPeriodToggle', true); // Initialize customDates with current range if it exists
        if (form.range.startDate && form.range.endDate) {
          customDates.value = getDatesInRange(form.range.startDate, form.range.endDate);
        } else if (customDates.value.length > 0) {
          // If we have previous custom dates, restore them to form.range
          form.range.startDate = customDates.value[0];
          form.range.endDate = customDates.value[customDates.value.length - 1];
        } else {
          // Clear out any existing custom range
          form.range.startDate = null;
          form.range.endDate = null;
          customDates.value = [];
        }
        emit('dateRangeFilter', form.range);
      } else {
        period.value = false;
        // Emit that custom period is now inactive
        emit('customPeriodToggle', false);
        // Find the matching preset
        const chosen = periods.value.find(p => p.value === newVal);
        if (chosen) {
          form.range = {
            ...chosen.range
          };
          emit('dateRangeFilter', form.range);
        }
      }
    });

    // 2) Watch customDates => if user picks a date range array, set start & end
    watch(customDates, dates => {
      // ‘dates’ is an array of all dates in the range
      if (Array.isArray(dates) && dates.length > 0) {
        form.range.startDate = dates[0];
        form.range.endDate = dates[dates.length - 1];
      } else {
        form.range.startDate = null;
        form.range.endDate = null;
      }
      // Emit the new range
      emit('dateRangeFilter', form.range);
    });

    // Toggles from the period pick => custom or back
    function periodChange() {
      if (!period.value) {
        // Switch to custom
        period.value = true;
        selectedId.value = 'custom';
      } else {
        // Switch to default
        period.value = false;
        selectedId.value = 'allTime';
      }
    }
    return (_ctx, _cache) => {
      return _openBlock(), _createElementBlock("div", _hoisted_1, [__props.layout === 'horizontal' ? (_openBlock(), _createElementBlock("div", _hoisted_2, [_createElementVNode("div", _hoisted_3, [_withDirectives(_createElementVNode("label", {
        class: "pr-2 py-1 text-sm"
      }, _toDisplayString(__props.defaultRange), 513), [[_vShow, !__props.rangeHideTitle]]), _createVNode(LlSelect, {
        modelValue: selectedId.value,
        "onUpdate:modelValue": _cache[0] || (_cache[0] = $event => selectedId.value = $event),
        items: periods.value,
        "item-title": "label",
        "item-value": "value",
        "return-object": false,
        "is-autocomplete": true,
        class: "at-choose-client remove-outline",
        placeholder: "Pick Time Period",
        "hide-details": "true"
      }, null, 8, ["modelValue", "items"])]), period.value ? (_openBlock(), _createBlock(LlDatePicker, {
        key: 0,
        modelValue: customDates.value,
        "onUpdate:modelValue": _cache[1] || (_cache[1] = $event => customDates.value = $event),
        multiple: "range",
        showButton: false,
        label: "Pick Date Range",
        labelClass: "text-sm py-1",
        showRangeLimitMessage: true,
        class: "flex-1 min-w-0"
      }, null, 8, ["modelValue"])) : _createCommentVNode("", true)])) : (_openBlock(), _createElementBlock(_Fragment, {
        key: 1
      }, [_withDirectives(_createElementVNode("label", {
        class: "pr-2 py-1 text-sm"
      }, _toDisplayString(__props.defaultRange), 513), [[_vShow, !__props.rangeHideTitle]]), _createVNode(LlSelect, {
        modelValue: selectedId.value,
        "onUpdate:modelValue": _cache[2] || (_cache[2] = $event => selectedId.value = $event),
        items: periods.value,
        "item-title": "label",
        "item-value": "value",
        "return-object": false,
        "is-autocomplete": true,
        class: "at-choose-client remove-outline",
        placeholder: "Pick Time Period",
        "hide-details": "true"
      }, null, 8, ["modelValue", "items"]), _createElementVNode("div", _hoisted_4, [period.value ? (_openBlock(), _createBlock(LlDatePicker, {
        key: 0,
        modelValue: customDates.value,
        "onUpdate:modelValue": _cache[3] || (_cache[3] = $event => customDates.value = $event),
        multiple: "range",
        showButton: false,
        label: "Pick Date Range",
        labelClass: "py-1",
        showRangeLimitMessage: true
      }, null, 8, ["modelValue"])) : _createCommentVNode("", true)])], 64))]);
    };
  }
};